import Dialog from "components/generic/Dialog";
import React, { useState } from "react";

import { useBulkUserImportsQuery } from "~/components/Admin/User/BulkImport/BulkImport.generated";
import { SecondaryButton } from "~/components/common/buttons";
import { DownloadIcon } from "~/components/common/icons";

interface Props {
  orgSlug: string;
  visible: boolean;
  close: () => void;
}

const PAGE_SIZE = 5;

const PastImports = ({ orgSlug }: { orgSlug: string }) => {
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;

  const { data, loading } = useBulkUserImportsQuery({
    variables: { orgSlug, limit: PAGE_SIZE, offset },
    fetchPolicy: "cache-and-network",
    skip: !orgSlug,
  });

  const imports = data?.bulkUserImports?.results ?? [];
  const count = data?.bulkUserImports?.count ?? 0;
  const totalPages = Math.ceil(count / PAGE_SIZE);

  if (loading && !data)
    return <p style={{ color: "#888", fontSize: "0.875rem" }}>Loading…</p>;
  if (count === 0)
    return (
      <p style={{ color: "#888", fontSize: "0.875rem" }}>
        No bulk imports yet.
      </p>
    );

  return (
    <>
      <table
        className="table table-sm table-bordered"
        style={{ fontSize: "0.875rem", marginBottom: 0 }}
      >
        <thead>
          <tr>
            <th>File</th>
            <th>Date</th>
            <th>New Users</th>
            <th>Invitations Sent</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {imports.map((imp) => (
            <tr key={imp.id}>
              <td
                style={{
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {imp.fileName}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {new Date(imp.importedAt).toLocaleDateString()}
              </td>
              <td>{imp.rowCounts.newUser}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {imp.invitationsSentAt
                  ? new Date(imp.invitationsSentAt).toLocaleDateString()
                  : "—"}
              </td>
              <td>
                <a
                  href={`/cleancapital/admin/${orgSlug}/bulk-imports/${imp.id}/export.xlsx`}
                  download
                  style={{ textDecoration: "none" }}
                >
                  <SecondaryButton
                    as="span"
                    style={{
                      padding: "2px 8px",
                      fontSize: "0.78rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Download export
                  </SecondaryButton>
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "0.5rem",
            marginTop: "0.5rem",
            fontSize: "0.8rem",
          }}
        >
          <SecondaryButton
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            style={{ padding: "2px 8px", fontSize: "0.75rem" }}
          >
            ‹ Prev
          </SecondaryButton>
          <span style={{ color: "#666" }}>
            {page} / {totalPages}
          </span>
          <SecondaryButton
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            style={{ padding: "2px 8px", fontSize: "0.75rem" }}
          >
            Next ›
          </SecondaryButton>
        </div>
      )}
    </>
  );
};

export const ExportDialog = ({ orgSlug, visible, close }: Props) => {
  const allUsersUrl = `/cleancapital/admin/${orgSlug}/users`;

  return (
    <Dialog
      header="Export"
      isOpen={visible}
      onRequestClose={close}
      style={{ content: { width: 680, maxWidth: "95vw" } }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {/* All users export */}
        <div>
          <p
            style={{
              margin: "0 0 0.5rem",
              fontWeight: 500,
              fontSize: "0.875rem",
              color: "#495057",
            }}
          >
            All users
          </p>
          <p
            style={{
              margin: "0 0 0.75rem",
              fontSize: "0.875rem",
              color: "#666",
            }}
          >
            Download a CSV of all current users and pending invitations for this
            organization.
          </p>
          <a href={allUsersUrl} download style={{ textDecoration: "none" }}>
            <SecondaryButton as="span">
              <DownloadIcon />
              <span>Download users (.csv)</span>
            </SecondaryButton>
          </a>
        </div>

        {/* Divider */}
        <hr style={{ margin: 0, borderColor: "#dee2e6" }} />

        {/* Past bulk imports */}
        <div>
          <p
            style={{
              margin: "0 0 0.75rem",
              fontWeight: 500,
              fontSize: "0.875rem",
              color: "#495057",
            }}
          >
            Bulk import exports
          </p>
          <PastImports orgSlug={orgSlug} />
        </div>

        <div className="d-flex justify-content-end">
          <SecondaryButton onClick={close}>Close</SecondaryButton>
        </div>
      </div>
    </Dialog>
  );
};
